import React, { useEffect } from 'react';
import { RestaurantDetails_getRestaurantDetails_menuCard } from '../../../../../types/graphql-types-generated.ts';
import { Dish } from '../dish/dish.tsx';
import { Ad } from '../../../../page/patterns/00-atoms/ad/ad.tsx';
import { isNonEmptyArray } from '../../../../../utils/array.tsx';
import { classList } from '../../../../../utils/string.ts';
import { safeWindow } from '../../../../../utils/window.ts';
import { dishFilter, dishFilterDefault } from '../menu-filter/menu-filter.tsx';
import {
  AllDishesType,
  sortLeastExpensive,
  sortMostExpensive,
  sortMostPopular
} from '../../../../../utils/dishes.ts';
import { scrollToTargetCenter } from '../../../../../utils/scroll.ts';
import { UBTracking } from '../../../../../components/tracking/ubtracking.ts';

/**
 * The ad frequency.
 */
const menusBeforeAd = 4;

export type MenuCardProps = {
  categories: RestaurantDetails_getRestaurantDetails_menuCard['categories'];
  sort?: string;
  filter?: string;
};

export const MenuCard: React.FunctionComponent<MenuCardProps> = ({
  categories,
  sort = 'default',
  filter = ''
}) => {
  const menuContainerRef = React.useRef<HTMLDivElement>(null);
  const menuSortActive = sort !== 'default';
  let allDishes: AllDishesType = [];
  let adCount = 0;
  let menusSinceLastAd = 0;
  let dishCount = 0;

  useEffect(() => {
    const menuCategoriesContainer = document.getElementById('menu-categories');
    const handleScroll = () => {
      if (menuContainerRef.current) {
        const { top } = menuContainerRef.current.getBoundingClientRect();

        if (top <= 0) {
          menuCategoriesContainer?.classList.remove('max-sm:hidden');
        } else {
          menuCategoriesContainer?.classList.add('max-sm:hidden');
        }
      }
    };

    safeWindow()?.addEventListener('scroll', handleScroll);

    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          const categoryElementUnderline = document.querySelector(
            `a[href="#${entry.target.id}"]>div`
          );
          if (entry.isIntersecting) {
            const menuCategories = menuCategoriesContainer?.querySelector('div');
            const categoryElement = document.querySelector(`a[href="#${entry.target.id}"]`);

            categoryElementUnderline?.classList.remove('hidden');
            if (categoryElement && menuCategories) {
              scrollToTargetCenter(categoryElement as HTMLElement, menuCategories as HTMLElement);
            }
          } else {
            categoryElementUnderline?.classList.add('hidden');
          }
        });
      },
      { root: null, rootMargin: '0px', threshold: 0.1 }
    );

    categories?.forEach(category => {
      const categoryElement = document.getElementById(`kategorie-${category?.id}`);
      if (categoryElement) {
        observer.observe(categoryElement);
      }
    });

    return () => {
      observer.disconnect();
    };
  }, []);

  if (menuSortActive) {
    allDishes = categories
      ?.flatMap(category => {
        if (category?.id == -1) {
          return null;
        }
        return category?.entries || [];
      })
      .filter(dishFilterDefault);

    switch (sort) {
      case 'most-popular':
        allDishes = sortMostPopular(allDishes);
        break;

      case 'most-expensive':
        allDishes = sortMostExpensive(allDishes);
        break;

      case 'least-expensive':
        allDishes = sortLeastExpensive(allDishes);
        break;
    }
  }

  function entriesToDisplay(entries: any[] | null | undefined) {
    return (
      <div className="max-sm:divide-sk-grey-200 max-sm:divide-y-[1px] md:grid md:grid-cols-2 md:gap-4">
        {isNonEmptyArray(entries) &&
          entries?.map((entry, index) => {
            if (!dishFilter(filter.split(','), entry)) {
              return;
            }
            dishCount++;

            const isEndOfRow = (index + 1) % 2 === 0;
            const shouldShowAd =
              (menusSinceLastAd >= menusBeforeAd - 1 && isEndOfRow) ||
              (index === entries.length - 1 && menusSinceLastAd > menusBeforeAd - 2);

            if (shouldShowAd) {
              adCount++;
              menusSinceLastAd = 0;
            } else {
              menusSinceLastAd++;
            }

            return (
              entry && (
                <React.Fragment key={entry.id}>
                  <Dish entry={entry} dishCount={dishCount} />
                  {/* {entries && entries.indexOf(entry) !== entries.length - 1 && (
                    <hr className="h-px border-0 bg-sk-grey-200 md:hidden" />
                  )} */}
                  {shouldShowAd && (
                    <div className="col-span-2 col-start-1 -mx-4 my-4">
                      <Ad
                        adId={`sk_info_content_${adCount}`}
                        minHeightClass={classList(
                          adCount < 4 && 'min-h-[600px]',
                          adCount >= 4 && 'min-h-[480px]',
                          adCount === 1 && 'w-full md:min-h-[480px]',
                          adCount > 1 && 'md:min-h-[400px]'
                        )}
                        className={classList(adCount >= 4 && 'ad-infinite')}
                        highlighted
                      />
                    </div>
                  )}
                </React.Fragment>
              )
            );
          })}
      </div>
    );
  }

  return (
    <>
      <div className={`bg-sk-grey-0 peer p-4 empty:hidden`} ref={menuContainerRef}>
        {menuSortActive && entriesToDisplay(allDishes)}
        {!menuSortActive &&
          categories
            ?.filter(category => {
              const entries = category?.entries || [];
              return entries.some(entry => dishFilter(filter.split(','), entry));
            })
            .map(category => {
              const entries = category?.entries;
              return (
                category && (
                  <div
                    key={category.id}
                    id={`kategorie-${category.id}`}
                    className="scroll-m-[123px] py-4"
                  >
                    <h3 className="my-4 text-xl font-extrabold">{category.name}</h3>
                    {entriesToDisplay(entries)}
                  </div>
                )
              );
            })}
      </div>
      <div className="bg-sk-grey-0 hidden p-4 peer-empty:block">
        <span className="text-xl font-extrabold">keine Gerichte verfügbar</span>
      </div>
    </>
  );
};
